import { useEffect, useMemo, useRef, useState } from "react";
import type { Factsheet } from "../lib/types";
import {
  factsheet,
  buddiesForSpecies,
  lookupSpecies,
} from "../lib/api";
import {
  refreshLifelistFromSpecies,
  useFishStore,
} from "../lib/storage";
import { sizedPhotoUrl } from "../lib/thumb";
import { useAuth } from "../lib/auth";
import { useModalChrome } from "../lib/modalUx";
import { CloseIcon, PinIcon, SceneFallback } from "../ui";
import {
  getCachedFactsheet,
  setCachedFactsheet,
} from "../lib/factsheetCache";
import { openPhoto } from "./PhotoViewer";
import { toast } from "./Toaster";
import type { BuddySighting } from "../../shared/types";
import { hasRealCommonName } from "../../shared/species";
import { logEvent } from "../lib/events";

// The detail view only needs identity + a photo + an optional "where logged"
// name — a full LifelistEntry satisfies this structurally, but a freshly
// listed Spot-result species can be adapted to it too, so the factsheet is
// reachable without first logging the fish.
export interface SpeciesDetailTarget {
  taxon_id: number;
  common_name: string;
  scientific_name: string;
  photo_url?: string | null;
  location_name?: string | null;
}

// Tap a species in the collection -> richer detail: description, IUCN status,
// diet/size/habitat/behavior, a world distribution map, and where YOU saw it.

// Web Mercator (EPSG:3857). The GBIF z0 tile is the whole world in one square
// image, so a lat/lng maps to a [0,1] fraction we can position a dot at.
function project(lat: number, lng: number): { x: number; y: number } {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const s = Math.sin((clamped * Math.PI) / 180);
  return {
    x: (lng + 180) / 360,
    y: 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI),
  };
}

export function SpeciesDetail({
  entry,
  onClose,
}: {
  entry: SpeciesDetailTarget | null;
  onClose: () => void;
}) {
  const [data, setData] = useState<Factsheet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const visited = useFishStore((s) => s.visited_locations);
  const auth = useAuth();
  const [overlap, setOverlap] = useState<BuddySighting[]>([]);
  const [overlapExpanded, setOverlapExpanded] = useState(false);

  // Places you've recorded this species: its lifelist location + any visited
  // spot whose cached list contains this taxon.
  const seenAt = useMemo(() => {
    if (!entry) return [];
    const names = new Set<string>();
    if (entry.location_name) names.add(entry.location_name);
    for (const v of visited) {
      if (v.cached_species.some((s) => s.taxon_id === entry.taxon_id)) {
        names.add(v.name);
      }
    }
    return [...names];
  }, [entry, visited]);

  // Same sightings, but only the ones we have coordinates for (visited spots),
  // so they can be plotted on the map. A logged location with no matching
  // visited entry has no lat/lng — it still shows as a chip below.
  const seenPoints = useMemo(() => {
    if (!entry) return [];
    const pts: { name: string; lat: number; lng: number }[] = [];
    for (const v of visited) {
      if (v.cached_species.some((s) => s.taxon_id === entry.taxon_id)) {
        pts.push({ name: v.name, lat: v.lat, lng: v.lng });
      }
    }
    return pts;
  }, [entry, visited]);

  useEffect(() => {
    if (!entry) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [entry, onClose]);

  useModalChrome(entry !== null, onClose, dialogRef);

  useEffect(() => {
    if (!entry) {
      setData(null);
      setError(null);
      return;
    }
    // Cache hit: skip the fetch and the loading state entirely so the
    // modal opens already populated.
    const cached = getCachedFactsheet(entry);
    if (cached) {
      setData(cached);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    factsheet({
      taxon_id: entry.taxon_id,
      common: entry.common_name,
      scientific: entry.scientific_name,
    })
      .then((d) => {
        if (cancelled) return;
        setCachedFactsheet(entry, d);
        setData(d);
      })
      .catch(
        (e: unknown) =>
          !cancelled &&
          setError(e instanceof Error ? e.message : "Couldn't load details."),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [entry]);

  // Buddy overlap — best-effort; a failing fetch leaves the section hidden.
  useEffect(() => {
    if (!entry || auth.status !== "authed") {
      setOverlap([]);
      setOverlapExpanded(false);
      return;
    }
    let cancelled = false;
    setOverlapExpanded(false);
    buddiesForSpecies(entry.scientific_name)
      .then((r) => {
        if (!cancelled) setOverlap(r.sightings);
      })
      .catch(() => {
        if (!cancelled) setOverlap([]);
      });
    return () => {
      cancelled = true;
    };
  }, [entry, auth.status]);

  // Broken-image fallback for the hero — mirrors SpeciesHero/PhotoFrame
  // so a stale iNat URL degrades to SceneFallback instead of iOS's
  // question-mark glyph. Reset when entry changes so a fresh photo
  // gets a fresh chance.
  const [photoErrored, setPhotoErrored] = useState(false);
  useEffect(() => {
    setPhotoErrored(false);
  }, [entry?.photo_url]);

  // "Wrong photo?" swap. Server returns the Wikipedia summary photo for
  // the scientific name; on hit, we patch the lifelist entry directly
  // (photo.swap event fires from storage). Only applies to entries the
  // user has actually lifelisted — swapping a Spot-screen preview
  // doesn't make sense because it's not persisted.
  const [swapping, setSwapping] = useState(false);
  const isInLifelist = useFishStore((s) =>
    entry ? s.lifelist.some((e) => e.taxon_id === entry.taxon_id) : false,
  );
  const onSwapPhoto = async () => {
    if (!entry || swapping) return;
    setSwapping(true);
    try {
      // The lookup endpoint returns EVERY field (photo, common name,
      // category) in one round-trip, so a "wrong photo?" tap is the
      // right place to opportunistically refresh the rest of the
      // entry's stale data too. Prevents the "Stenella attenuata"-
      // shape bug where an entry saved off a bare OBIS candidate had
      // no common name / "Other" category even though Wikipedia knew
      // it as "Pantropical spotted dolphin / Mammals". Fields only
      // update when the fresh value is strictly better than the
      // current one — see refreshLifelistFromSpecies.
      const fresh = await lookupSpecies(entry.scientific_name);
      const changed = refreshLifelistFromSpecies(entry.taxon_id, fresh);
      if (
        !changed.photo_changed &&
        !changed.common_name_changed &&
        !changed.category_changed
      ) {
        toast("Wikipedia doesn't have better info for this species.");
        logEvent("photo.swap.miss", {
          taxon_id: entry.taxon_id,
          scientific_name: entry.scientific_name,
          fresh_has_photo: !!fresh.photo_url,
          fresh_common_name: fresh.common_name,
        });
        return;
      }
      if (changed.photo_changed) setPhotoErrored(false);
      const parts: string[] = [];
      if (changed.photo_changed) parts.push("photo");
      if (changed.common_name_changed) parts.push("name");
      if (changed.category_changed) parts.push("category");
      toast(`Refreshed ${parts.join(", ")} from Wikipedia.`);
    } catch (e) {
      // Distinguish "Wikipedia has nothing" (a real answer) from "we
      // couldn't reach Wikipedia" (transient outage). The lookup path
      // throws only on the latter; a no-hit resolves with a bare
      // synthetic entry that refreshLifelistFromSpecies then flags as
      // no-change above.
      logEvent("photo.swap.error", {
        taxon_id: entry.taxon_id,
        scientific_name: entry.scientific_name,
        message: e instanceof Error ? e.message : String(e),
      });
      toast(
        e instanceof Error
          ? `Couldn't reach Wikipedia (${e.message}). Try again shortly.`
          : "Couldn't reach Wikipedia — try again shortly.",
      );
    } finally {
      setSwapping(false);
    }
  };

  // Suppress the italic subtitle when common_name is the merge-fallback
  // (equals scientific). Same rule as SpeciesHero; without it we'd
  // render bold + italic duplicates of the scientific name — the exact
  // bug the observability trace flagged on the spinner dolphin card.
  const hasCommon = entry
    ? hasRealCommonName(entry.common_name, entry.scientific_name)
    : true;
  const titleText = entry
    ? hasCommon
      ? entry.common_name
      : entry.scientific_name
    : "";

  // Diagnostic breadcrumbs — same shape as the SpeciesHero events so
  // the /debug timeline can compare surfaces.
  const useScene = !entry?.photo_url || photoErrored;
  useEffect(() => {
    if (!entry) return;
    if (useScene) {
      logEvent("presentation.photo.fallback", {
        surface: "detail",
        scientific_name: entry.scientific_name,
        common_name: entry.common_name,
        photo_url: entry.photo_url ?? "",
        reason: entry.photo_url ? "load-error" : "no-url",
      });
    }
    if (!hasCommon) {
      logEvent("presentation.common_name.fallback", {
        surface: "detail",
        scientific_name: entry.scientific_name,
        common_name: entry.common_name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.taxon_id, entry?.photo_url, hasCommon, photoErrored]);

  if (!entry) return null;

  const key = data?.gbif_taxon_key ?? null;
  const facts: [string, string][] = data
    ? ([
        ["Diet", data.diet],
        ["Size", data.size],
        ["Habitat", data.habitat],
        ["Behavior", data.behavior],
        ["Where found", data.where_found],
      ].filter(([, v]) => v) as [string, string][])
    : [];

  // Surface only the source outcomes worth telling the user about — a normal
  // "skipped"/"empty" iNat for a non-iNat taxon isn't a failure to report.
  const src = data?.sources;
  const aiBlurb = src?.description === "ai";
  const sourceIssues: string[] = [];
  if (src?.inat === "error") sourceIssues.push("iNaturalist data unavailable");
  if (src?.gbif === "error") sourceIssues.push("distribution map unavailable");
  if (src?.ai === "error")
    sourceIssues.push("AI facts unavailable (model error or token limit)");
  else if (src?.ai === "empty")
    sourceIssues.push("AI facts unavailable (model not confident)");
  if (src?.description === "none") sourceIssues.push("no description found");

  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center bg-ink/40 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="flex max-h-[92dvh] w-full max-w-lg flex-col overflow-hidden overscroll-contain rounded-t-3xl bg-paper pb-[env(safe-area-inset-bottom)] shadow-card focus:outline-none sm:rounded-3xl lg:max-w-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={titleText}
      >
        <div className="relative">
          <button
            type="button"
            onClick={() =>
              entry.photo_url &&
              !photoErrored &&
              openPhoto({
                url: entry.photo_url,
                common_name: entry.common_name,
                scientific_name: entry.scientific_name,
              })
            }
            className="block aspect-[16/9] w-full bg-ink"
            aria-label={`Enlarge photo of ${titleText}`}
          >
            {useScene ? (
              <SceneFallback
                hint={entry.scientific_name}
                className="absolute inset-0"
                vignette
              />
            ) : (
              <img
                src={sizedPhotoUrl(entry.photo_url ?? "", "medium")}
                alt={titleText}
                onError={() => setPhotoErrored(true)}
                className="h-full w-full object-cover"
              />
            )}
          </button>
          <div className="scrim-hero-short pointer-events-none absolute inset-x-0 bottom-0 p-4 pt-10">
            <p
              className={`font-display text-xl font-bold text-white${
                hasCommon ? "" : " italic"
              }`}
            >
              {titleText}
            </p>
            {hasCommon && (
              <p className="text-sm italic text-white/65">
                {entry.scientific_name}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full bg-ink/50 text-white/80 backdrop-blur-sm hover:bg-ink/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Close"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
          {isInLifelist && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void onSwapPhoto();
              }}
              disabled={swapping}
              aria-label="Wrong photo? Try Wikipedia"
              className="absolute right-2 top-16 inline-flex min-h-[44px] items-center gap-1 rounded-full bg-ink/55 px-4 py-2 text-[11.5px] font-semibold text-white/85 backdrop-blur-sm transition hover:bg-ink/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-60"
            >
              {swapping ? "Fetching…" : "Wrong photo?"}
            </button>
          )}
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {loading && (
            <p className="py-4 text-center text-sm text-ink/55">
              Loading details…
            </p>
          )}
          {error && (
            <p className="rounded-xl bg-coral/10 px-4 py-3 text-sm text-coral">
              {error}
            </p>
          )}

          {data && (
            <>
              {(data.conservation || data.endangered) && (
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                    data.endangered
                      ? "bg-coral/15 text-coral ring-1 ring-inset ring-coral/40"
                      : "bg-aqua/15 text-aqua ring-1 ring-inset ring-aqua/30"
                  }`}
                >
                  {data.conservation || "Conservation status unknown"}
                </span>
              )}

              {data.description && (
                <div>
                  <p className="text-sm leading-relaxed text-ink/80">
                    {data.description}
                    {/^https:\/\//i.test(data.source_url ?? "") && (
                      <>
                        {" "}
                        <a
                          href={data.source_url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-semibold text-aqua-deep hover:underline"
                        >
                          Wikipedia ↗
                        </a>
                      </>
                    )}
                  </p>
                  {aiBlurb && (
                    <p className="mt-1 text-[11px] text-ink/60">
                      This description is AI-generated (no encyclopedia entry
                      found) and may be imprecise.
                    </p>
                  )}
                </div>
              )}

              {facts.length > 0 && (
                <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {facts.map(([k, v]) => (
                    <div
                      key={k}
                      className="rounded-xl bg-white/70 p-3 ring-1 ring-inset ring-ink/5"
                    >
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink/60">
                        {k}
                      </dt>
                      <dd className="mt-0.5 text-sm text-ink/80">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}

              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-ink/60">
                  Where it's found
                </h3>
                {/* Always render the map: even with no GBIF layer the ocean
                    backdrop + your own sighting dots are worth showing. Each
                    GBIF img hides itself onError so a CDN miss degrades to
                    ocean rather than a silent blank box. */}
                <div className="relative mt-2 aspect-square overflow-hidden rounded-xl bg-aqua/15 ring-1 ring-inset ring-ring">
                  <img
                    src="https://tile.gbif.org/3857/omt/0/0/0@1x.png?style=gbif-light"
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                  {key && (
                    <img
                      key={key}
                      src={`https://api.gbif.org/v2/map/occurrence/density/0/0/0@1x.png?srs=EPSG:3857&taxonKey=${key}&bin=hex&hexPerTile=51&style=classic.poly`}
                      alt={`Distribution of ${entry.common_name}`}
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  )}
                  {seenPoints.map((p, i) => {
                    const { x, y } = project(p.lat, p.lng);
                    return (
                      <span
                        key={`${p.name}-${i}`}
                        title={`You saw it here: ${p.name}`}
                        className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
                        style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
                      >
                        <span className="block h-3 w-3 rounded-full border-2 border-white bg-coral shadow-md" />
                      </span>
                    );
                  })}
                </div>
                <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-ink/60">
                  <span>
                    {key
                      ? "Global occurrences · GBIF"
                      : "Distribution data unavailable"}
                  </span>
                  {seenPoints.length > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block h-2 w-2 rounded-full border border-white bg-coral" />
                      your sightings
                    </span>
                  )}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-ink/60">
                  Where you've seen it
                </h3>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {seenAt.length > 0 ? (
                    seenAt.map((n) => (
                      <span
                        key={n}
                        className="rounded-full bg-aqua-deep/10 px-2.5 py-1 text-xs font-semibold text-aqua-deep"
                      >
                        <PinIcon className="inline h-3.5 w-3.5 mr-0.5 text-aqua-deep" />{n}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-ink/55">—</span>
                  )}
                </div>
              </div>

              {overlap.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-ink/60">
                    Also seen by your buddies
                  </h3>
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {(overlapExpanded ? overlap : overlap.slice(0, 3)).map(
                      (b) => {
                        const p = b.profile;
                        const name =
                          p.display_name ||
                          (p.username ? `@${p.username}` : "Someone");
                        return (
                          <li
                            key={`${p.user_id}-${b.taxon_id}`}
                            className="flex items-center gap-3 rounded-xl bg-white/70 px-3 py-2 ring-1 ring-inset ring-ink/5"
                          >
                            <span className="inline-block h-8 w-8 rounded-full bg-aqua/25 text-aqua-deep text-center text-xs font-bold leading-8">
                              {name[0]?.toUpperCase() ?? "?"}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold text-ink">
                                {name}
                              </div>
                              {b.location_name && (
                                <div className="truncate text-xs text-ink/55">
                                  <PinIcon className="inline h-3.5 w-3.5 mr-0.5 text-aqua-deep" />{b.location_name}
                                </div>
                              )}
                            </div>
                            {b.photo_url && (
                              <img
                                src={b.photo_url}
                                alt=""
                                className="h-10 w-10 rounded-lg object-cover ring-1 ring-inset ring-ink/10"
                                loading="lazy"
                              />
                            )}
                          </li>
                        );
                      },
                    )}
                  </ul>
                  {overlap.length > 3 && !overlapExpanded && (
                    <button
                      type="button"
                      onClick={() => setOverlapExpanded(true)}
                      className="mt-2 text-xs font-semibold text-aqua-deep hover:underline"
                    >
                      Show {overlap.length - 3} more
                    </button>
                  )}
                </div>
              )}

              {data.ai_generated && facts.length > 0 && (
                <p className="text-[11px] text-ink/60">
                  Diet/size/habitat/behavior are AI-generated and may be
                  imprecise.
                </p>
              )}
              {sourceIssues.length > 0 && (
                <p className="text-[11px] text-ink/60">
                  Sources: {sourceIssues.join(" · ")}.
                </p>
              )}
              <p className="text-[11px] text-ink/55">
                {data.cached
                  ? "Cached species detail (no fresh AI/API calls)."
                  : "Freshly fetched — cached for next time."}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
